/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect } from "react";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import ProductsList from "../components/booking/product/ProductsList";
import { useCart } from "../hooks/useCart";
import { useProductsWithSearchSort } from "../hooks/useProductsWithSearchSort";
import { useStepManager } from "../hooks/useStepManager";
import Text from "../components/ui/Text";
import Icon from "../components/ui/Icon";
import styled from "styled-components";
import Button from "../components/ui/Button";
import Flex from "../components/ui/Flex";
import { CART_LINE_TYPES, ROUTES } from "../constants/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { FlowType, setFlow } from "../store/slices/flowSlice";
import { setSelectedCategory } from "../store/slices/productsSlice";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { useSelectedCategory } from "../hooks/useSelectedCategory";
import { StyledFooterStepButton } from "../components/FooterStepButton";
import { queryKeys } from "../lib/react-query/queryKeys";

const BreadcrumbFlex = styled(Flex)`
  margin: 16px 0;
`;

const BreadcrumbLink = styled(Text)`
  cursor: pointer;
  white-space: nowrap;
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
      color: #000;
    }
  }
`;

const ProductsForSale = () => {
  const queryClient = useQueryClient();
  const {
    lines,
    productLines,
    findLine,
    addLine,
    updateLineParams,
    removeLine,
    setEntryRoute,
  } = useCart();
  const selectedCategory = useSelectedCategory();
  const { products, isLoading, categoriesWithProducts, searchSortProps } = useProductsWithSearchSort({
    categoryId: selectedCategory?.id ?? null,
    forSale: true,
  });
  const { navigateToPreviousStep } = useStepManager();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.products.list({ categoryId: selectedCategory?.id ?? null, standaloneOnly: true }),
    });
  }, [queryClient, selectedCategory]);

  useEffect(() => {
    setEntryRoute(location.pathname);
  }, [setEntryRoute, location.pathname]);

  useEffect(() => {
    if (!selectedCategory) {
      dispatch(setFlow(FlowType.CART));
    }
  }, [dispatch, selectedCategory]);

  const clearSelectedCategory = () => {
    dispatch(setSelectedCategory(null));
    navigateToPreviousStep();
  };

  const findProductLine = (productId) =>
    findLine(CART_LINE_TYPES.PRODUCT, (params) => +params.product_id === +productId);

  const setProductQty = (product, qty) => {
    const existing = findProductLine(product.id);
    if (qty <= 0) {
      if (existing) removeLine({ clientId: existing.clientId });
      return;
    }
    if (existing) {
      updateLineParams({ clientId: existing.clientId, patch: { qty } });
      return;
    }
    addLine({
      type: CART_LINE_TYPES.PRODUCT,
      entity: product,
      params: { product_id: product.id, qty },
    });
  };

  const handleProductSelect = (_position, product) => {
    setProductQty(product, findProductLine(product.id) ? 0 : 1);
  };

  const handleProductUpdate = (_position, product, newQty) => {
    setProductQty(product, newQty);
  };

  const handleCheckoutClick = () => {
    navigate(ROUTES.ORDER_SUMMARY);
  };

  const currentCategory = selectedCategory
    ? categoriesWithProducts?.find((c) => c.id === selectedCategory.id)
    : null;

  const allProducts = currentCategory ? currentCategory.products : products[0] ?? [];

  const selectedProductsView = productLines.map((line) => ({
    id: line.params.product_id,
    qty: line.params.qty,
  }));

  return (
    <Container>
      <NavigationHeader title="Products" cart clientLogin navigation />
      {selectedCategory && (
        <BreadcrumbFlex $align="center" $gap="0.3rem">
          <BreadcrumbLink onClick={clearSelectedCategory} children="All categories" />
          <Icon.ChevronRight size={Icon.SIZE.SM} />
          <Text color="black" children={selectedCategory.name} lineClamp={1} />
        </BreadcrumbFlex>
      )}

      <ProductsList
        {...searchSortProps}
        productsForSale={[allProducts]}
        isLoading={isLoading}
        selectedProducts={selectedProductsView}
        handleProductSelect={handleProductSelect}
        updateProduct={handleProductUpdate}
      />
      {lines.length > 0 && (
        <StyledFooterStepButton>
          <Button title="Go to checkout" fullWidth onClick={handleCheckoutClick} />
        </StyledFooterStepButton>
      )}
    </Container>
  );
};

export default ProductsForSale;
